/**
 * Canonical enumerations shared by server and client.
 *
 * Values are stored in SQLite exactly as written here. Changing a string is a
 * data migration, not a rename.
 */

export const PIPELINE_STATUSES = [
  'New',
  'Ready to Call',
  'Attempted',
  'Contacted',
  'Interested',
  'Examples Sent',
  'Follow-Up',
  'Proposal Sent',
  'Sold',
  'Not Interested',
  'Bad Lead',
] as const;
export type PipelineStatus = (typeof PIPELINE_STATUSES)[number];

/** Terminal statuses that should not appear in default calling queues. */
export const CLOSED_PIPELINE_STATUSES: PipelineStatus[] = ['Sold', 'Not Interested', 'Bad Lead'];

export const WEB_PRESENCE_STATUSES = [
  'Unknown',
  'No Website Found',
  'Facebook Only',
  'Google Profile Only',
  'Directory Only',
  'Official Website Found',
] as const;
export type WebPresenceStatus = (typeof WEB_PRESENCE_STATUSES)[number];

export const WEBSITE_CONDITIONS = [
  'Unknown',
  'Parked Domain',
  'Expired Domain',
  'Unreachable or Broken',
  'Outdated Website',
  'Weak Website',
  'Functioning Website',
] as const;
export type WebsiteCondition = (typeof WEBSITE_CONDITIONS)[number];

export const WEBSITE_CONDITION_TOOLTIPS: Record<WebsiteCondition, string> = {
  Unknown: 'Not yet assessed. No claim is being made about this site.',
  'Parked Domain': 'Domain resolves to a registrar or ad placeholder page rather than a real site.',
  'Expired Domain': 'Domain registration has lapsed or the site no longer resolves to an owner.',
  'Unreachable or Broken':
    'Site fails to load, returns a server error, or has a fatal certificate or DNS problem.',
  'Outdated Website':
    'Site loads but is visibly dated: old copyright, stale content, or pre-responsive layout.',
  'Weak Website':
    'Site works but converts poorly: thin content, unclear calls to action, or weak contact paths.',
  'Functioning Website':
    'Site loads correctly, is reasonably current, and presents no obvious conversion defects.',
};

export const NOTE_TYPES = ['General', 'Call', 'Research', 'Objection', 'Follow-Up'] as const;
export type NoteType = (typeof NOTE_TYPES)[number];

export const ACTIVITY_TYPES = [
  'lead_created',
  'lead_imported',
  'lead_updated',
  'pipeline_status_changed',
  'dnc_applied',
  'dnc_removed',
  'call_attempted',
  'note_added',
  'follow_up_created',
  'follow_up_completed',
  'examples_sent',
  'proposal_sent',
  'sale_completed',
  'duplicate_candidate_created',
  'duplicate_merged',
  'website_added',
  'website_audit_added',
  'website_issue_added',
  'website_issue_resolved',
  'import_rejected',
  'import_undone',
] as const;
export type ActivityType = (typeof ACTIVITY_TYPES)[number];

export const ACTIVITY_LABELS: Record<ActivityType, string> = {
  lead_created: 'Lead created',
  lead_imported: 'Lead imported',
  lead_updated: 'Lead updated',
  pipeline_status_changed: 'Pipeline status changed',
  dnc_applied: 'Do Not Contact applied',
  dnc_removed: 'Do Not Contact removed',
  call_attempted: 'Call attempted',
  note_added: 'Note added',
  follow_up_created: 'Follow-up created',
  follow_up_completed: 'Follow-up completed',
  examples_sent: 'Examples sent',
  proposal_sent: 'Proposal sent',
  sale_completed: 'Sale completed',
  duplicate_candidate_created: 'Duplicate candidate created',
  duplicate_merged: 'Duplicate merged',
  website_added: 'Website added',
  website_audit_added: 'Website audit added',
  website_issue_added: 'Website issue added',
  website_issue_resolved: 'Website issue resolved',
  import_rejected: 'Import rejected',
  import_undone: 'Import undone',
};

export const SOURCE_TYPES = [
  'Google Maps',
  'Google Search',
  'Facebook',
  'BBB',
  'Yelp',
  'Directory',
  'Referral',
  'Manual',
  'Import',
  'Other',
] as const;
export type SourceType = (typeof SOURCE_TYPES)[number];

/** Source types that count as "directory presence" for web-presence precedence. */
export const DIRECTORY_SOURCE_TYPES: SourceType[] = ['BBB', 'Yelp', 'Directory'];

export const FOLLOW_UP_TYPES = [
  'Call',
  'Text',
  'Email',
  'Send Examples',
  'Send Proposal',
  'Check Back',
  'General Reminder',
] as const;
export type FollowUpType = (typeof FOLLOW_UP_TYPES)[number];

export const FOLLOW_UP_STATUSES = ['Open', 'Completed', 'Snoozed', 'Cancelled'] as const;
export type FollowUpStatus = (typeof FOLLOW_UP_STATUSES)[number];

export const FOLLOW_UP_PRIORITIES = ['Low', 'Normal', 'High', 'Urgent'] as const;
export type FollowUpPriority = (typeof FOLLOW_UP_PRIORITIES)[number];

export const WEBSITE_PROJECT_STATUSES = [
  'Not Started',
  'Waiting for Content',
  'In Progress',
  'Review',
  'Live',
  'On Hold',
  'Completed',
] as const;
export type WebsiteProjectStatus = (typeof WEBSITE_PROJECT_STATUSES)[number];

export const HOSTING_STATUSES = ['None', 'Pending', 'Active', 'Lapsed', 'Cancelled'] as const;
export type HostingStatus = (typeof HOSTING_STATUSES)[number];

export const PAYMENT_TYPES = [
  'Deposit',
  'Installment',
  'Final Payment',
  'Hosting',
  'Refund',
  'Adjustment',
  'Other',
] as const;
export type PaymentType = (typeof PAYMENT_TYPES)[number];

export const ISSUE_SEVERITIES = ['Informational', 'Low', 'Medium', 'High', 'Critical'] as const;
export type IssueSeverity = (typeof ISSUE_SEVERITIES)[number];

export const SEVERITY_RANK: Record<IssueSeverity, number> = {
  Informational: 0,
  Low: 1,
  Medium: 2,
  High: 3,
  Critical: 4,
};

export const ISSUE_CONFIDENCES = ['Low', 'Medium', 'High', 'Verified'] as const;
export type IssueConfidence = (typeof ISSUE_CONFIDENCES)[number];

export const CONFIDENCE_MULTIPLIER: Record<IssueConfidence, number> = {
  Low: 0.4,
  Medium: 0.7,
  High: 1.0,
  Verified: 1.15,
};

export const ISSUE_STATUSES = [
  'Open',
  'Confirmed',
  'Needs Review',
  'Resolved',
  'False Positive',
  'Ignored',
] as const;
export type IssueStatus = (typeof ISSUE_STATUSES)[number];

/** Issue statuses that represent a live, scoreable problem. */
export const OPEN_ISSUE_STATUSES: IssueStatus[] = ['Open', 'Confirmed', 'Needs Review'];

export const AUDIT_SOURCES = ['imported', 'manual', 'external_tool'] as const;
export type AuditSource = (typeof AUDIT_SOURCES)[number];

export const WEBSITE_ISSUE_CATEGORIES = [
  'search_visibility',
  'lead_capture',
  'links_and_pages',
  'layout_usability',
  'technical_availability',
  'conversion',
] as const;
export type WebsiteIssueCategory = (typeof WEBSITE_ISSUE_CATEGORIES)[number];

export const WEBSITE_ISSUE_CATEGORY_LABELS: Record<WebsiteIssueCategory, string> = {
  search_visibility: 'Search visibility and indexing',
  lead_capture: 'Lead capture and forms',
  links_and_pages: 'Links and pages',
  layout_usability: 'Layout and usability',
  technical_availability: 'Technical availability',
  conversion: 'Conversion weaknesses',
};

export const EVIDENCE_TYPES = [
  'none',
  'url',
  'http_status',
  'header',
  'html_snippet',
  'screenshot_path',
  'text',
  'json',
] as const;
export type EvidenceType = (typeof EVIDENCE_TYPES)[number];

/**
 * Indexing claim vocabulary. LeadForge must never assert a site was "never
 * submitted to Google"; these are the only defensible statements.
 */
export const INDEXING_CLAIMS = [
  'Confirmed noindex directive',
  'Confirmed robots.txt block',
  'No indexed pages observed publicly',
  'Indexing status unknown',
  'Search Console submission cannot be verified',
] as const;
export type IndexingClaim = (typeof INDEXING_CLAIMS)[number];

export const IMPORT_STATUSES = [
  'pending',
  'mapping',
  'validating',
  'ready',
  'importing',
  'completed',
  'failed',
  'undone',
  'cancelled',
] as const;
export type ImportStatus = (typeof IMPORT_STATUSES)[number];

export const IMPORT_ROW_ACTIONS = [
  'inserted',
  'updated',
  'duplicate_candidate',
  'skipped',
  'rejected',
] as const;
export type ImportRowAction = (typeof IMPORT_ROW_ACTIONS)[number];

export const IMPORT_SOURCE_KINDS = ['upload', 'watched_folder'] as const;
export type ImportSourceKind = (typeof IMPORT_SOURCE_KINDS)[number];

export const IMPORT_FILE_KINDS = ['csv', 'json', 'jsonl'] as const;
export type ImportFileKind = (typeof IMPORT_FILE_KINDS)[number];

export const DUPLICATE_MATCH_TYPES = [
  'google_place_id',
  'normalized_phone',
  'name_plus_address',
  'source_url',
  'website_domain',
  'email',
  'profile_url',
  /** Candidate-only. Never auto-updates and never auto-merges. */
  'name_plus_city',
  'fuzzy_name_city',
  'fuzzy_name_phone',
  'fuzzy_owner_address',
  'fuzzy_profile_url',
  'fuzzy_domain',
  'fuzzy_shared_address',
] as const;
export type DuplicateMatchType = (typeof DUPLICATE_MATCH_TYPES)[number];

/** Match types produced by exact (Slice B) detection rather than fuzzy (Slice E). */
export const EXACT_MATCH_TYPES: DuplicateMatchType[] = [
  'google_place_id',
  'normalized_phone',
  'name_plus_address',
  'source_url',
  'website_domain',
  'email',
  'profile_url',
  'name_plus_city',
];

/**
 * Match types that may only ever produce a duplicate-review candidate. A rule
 * listed here must never resolve to an automatic update or merge, however
 * compatible the two records look.
 */
export const CANDIDATE_ONLY_MATCH_TYPES: DuplicateMatchType[] = ['name_plus_city'];

export const DUPLICATE_STATES = ['pending', 'resolved'] as const;
export type DuplicateState = (typeof DUPLICATE_STATES)[number];

export const DUPLICATE_RESOLUTIONS = [
  'merged',
  'kept_both',
  'kept_both_suppressed',
  'separate_location',
  'approved_update',
  'inserted_separate',
  'inserted_reassigned_place_id',
  'skipped',
  'rejected',
] as const;
export type DuplicateResolution = (typeof DUPLICATE_RESOLUTIONS)[number];

/** [v5] Sub-choices when "Insert anyway" collides with the Place ID unique index. */
export const PLACE_ID_CONFLICT_CHOICES = [
  'separate_location',
  'unrelated_business',
  'reassign_place_id',
] as const;
export type PlaceIdConflictChoice = (typeof PLACE_ID_CONFLICT_CHOICES)[number];

export const ACTOR_TYPES = ['user', 'worker', 'system'] as const;
export type ActorType = (typeof ACTOR_TYPES)[number];

export const JOB_TYPES = [
  'score_recalculation',
  'website_summary_backfill',
  'watched_folder_import',
] as const;
export type JobType = (typeof JOB_TYPES)[number];

export const JOB_STATUSES = [
  'queued',
  'running',
  'completed',
  'failed',
  'cancelled',
  'cancelling',
] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];

export const CALL_PROTOCOLS = ['tel', 'callto', 'sip', 'copy'] as const;
export type CallProtocol = (typeof CALL_PROTOCOLS)[number];

export const QUEUE_ITEM_STATES = ['pending', 'consumed', 'skipped', 'removed'] as const;
export type QueueItemState = (typeof QUEUE_ITEM_STATES)[number];

export const QUEUE_REMOVAL_REASONS = [
  'dnc',
  'archived',
  'merged',
  'cooldown',
  'no_phone',
  'research_quarantine',
  'manual',
  /** Reached a terminal pipeline status — see CLOSED_PIPELINE_STATUSES. */
  'closed',
  /** Already called, dispositioned or scheduled for follow-up. */
  'worked',
] as const;
export type QueueRemovalReason = (typeof QUEUE_REMOVAL_REASONS)[number];

export const CALL_SESSION_STATUSES = ['active', 'paused', 'completed', 'abandoned'] as const;
export type CallSessionStatus = (typeof CALL_SESSION_STATUSES)[number];

/** How precisely a business start date is known. */
export const DATE_PRECISIONS = ["day", "month", "year"] as const;
export type DatePrecision = (typeof DATE_PRECISIONS)[number];

export const US_STATES = [
  'OH',
  'IN',
  'KY',
  'MI',
  'PA',
  'WV',
  'IL',
  'WI',
  'MO',
  'TN',
] as const;

/**
 * Authentication roles. Two, deliberately: `admin` is the owner, `va` is a
 * restricted caller. Custom roles are out of scope — widening this list is a
 * migration, which is the right amount of friction.
 */
export const USER_ROLES = ['admin', 'va'] as const;
export type UserRole = (typeof USER_ROLES)[number];

// ---------------------------------------------------------------------------
// Phase 5 — phone lines
// ---------------------------------------------------------------------------

/**
 * Where a number is in its working life.
 *
 * Mirrors the CHECK constraint in migration 0014; the database is the
 * authority and this list must match it exactly.
 *
 *   active   — the number this agent currently dials from. At most one per
 *              agent, enforced by a partial unique index.
 *   warming  — in use at low volume to build a call history before it carries
 *              a full queue. A brand new number starts here.
 *   flagged  — a carrier or reputation service is labelling it. Stop using it.
 *   paused   — deliberately rested, usually because it was just rotated out.
 *   backup   — ready to take over, not currently dialling.
 *   retired  — finished. Terminal: a retired line never comes back, because
 *              the history of what it did while active has to stay meaningful.
 */
export const PHONE_LINE_STATUSES = [
  'active',
  'warming',
  'flagged',
  'paused',
  'backup',
  'retired',
] as const;
export type PhoneLineStatus = (typeof PHONE_LINE_STATUSES)[number];

/** Statuses a line may still move to. Retired is absent on purpose. */
export const PHONE_LINE_REACTIVATABLE_STATUSES = PHONE_LINE_STATUSES.filter(
  (s) => s !== 'retired',
);

export const PHONE_LINE_STATUS_LABELS: Record<PhoneLineStatus, string> = {
  active: 'Active',
  warming: 'Warming up',
  flagged: 'Flagged',
  paused: 'Paused',
  backup: 'Backup',
  retired: 'Retired',
};

/**
 * Where a reputation observation came from.
 *
 * `carrier_manual` is someone reading a carrier's own lookup page;
 * `provider_api` is a telephony provider reporting on its own number. Both are
 * manual entry in this phase — `method` is what says whether a human typed it.
 */
export const REPUTATION_SOURCES = [
  'hiya',
  'tns',
  'first_orion',
  'carrier_manual',
  'provider_api',
  'other',
] as const;
export type ReputationSource = (typeof REPUTATION_SOURCES)[number];

export const REPUTATION_SOURCE_LABELS: Record<ReputationSource, string> = {
  hiya: 'Hiya',
  tns: 'TNS',
  first_orion: 'First Orion',
  carrier_manual: 'Carrier lookup (manual)',
  provider_api: 'Provider API',
  other: 'Other',
};

export const REPUTATION_METHODS = ['manual', 'api'] as const;
export type ReputationMethod = (typeof REPUTATION_METHODS)[number];

/**
 * What a calling provider can actually do.
 *
 * The only implementation in this phase is `handoff`, which is what LeadForge
 * has always done: build a tel:/callto:/sip: URI, or offer the number to copy,
 * and let the operating system or the phone on the desk place the call. It
 * reports every server-side capability as false, because it has none of them —
 * claiming otherwise would make a report look authoritative when the duration
 * in it was inferred from when someone closed a form.
 */
export const CALL_PROVIDER_KINDS = ['handoff'] as const;
export type CallProviderKind = (typeof CALL_PROVIDER_KINDS)[number];

/** Reasons the admin phone-line warnings fire. */
export const PHONE_LINE_WARNING_KINDS = [
  'reputation_stale',
  'daily_cap_reached',
  'no_backup',
  'flagged',
] as const;
export type PhoneLineWarningKind = (typeof PHONE_LINE_WARNING_KINDS)[number];

// ---------------------------------------------------------------------------
// Phase 6 — interface scale
// ---------------------------------------------------------------------------

/**
 * Interface scale, as a percentage of the default root font size.
 *
 * Four steps, not a free range. 90% is unreadable on a tool this dense, and
 * past 140% the calling workspace stops fitting at 1440x900 — which is the
 * resolution it is laid out for. Four is also few enough that every screen can
 * actually be checked at every one of them.
 *
 * Mirrors the CHECK constraint in migration 0015; the database is the
 * authority and this list must match it exactly.
 */
export const UI_SCALES = [100, 110, 125, 140] as const;
export type UiScale = (typeof UI_SCALES)[number];

export const DEFAULT_UI_SCALE: UiScale = 100;

export const UI_SCALE_LABELS: Record<UiScale, string> = {
  100: '100%',
  110: '110%',
  125: '125%',
  140: '140%',
};
